import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, CreditCard, ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';

export default function Subscription() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [params] = useSearchParams();

  const load = async () => {
    try { const r = await api.get('/me/'); setMe(r.data); } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const session_id = params.get('session_id');
    if (session_id) {
      setInfo('Verifying payment...');
      api.get(`/stripe/checkout/status/${session_id}`).then(() => {
        setInfo('Subscription activated. You are now defending the land.');
        load();
      }).catch(e => setErr(e.response?.data?.detail || 'Payment verification failed'));
    }
    if (params.get('cancelled')) setInfo('Checkout cancelled. No charge was made.');
  }, [params]);

  const subscribe = async () => {
    setErr(''); setBusy(true);
    try {
      const r = await api.post('/stripe/checkout', { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message); setBusy(false);
    }
  };

  const portal = async () => {
    setErr(''); setBusy(true);
    try {
      const r = await api.post('/stripe/portal', { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message); setBusy(false);
    }
  };

  const sub = me?.profile?.subscription_active;
  const status = me?.profile?.subscription_status || 'inactive';

  return (
    <div className="min-h-screen bg-plai-bg flex flex-col" data-testid="subscription-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard"><Logo size="sm" /></Link>
          <Link to="/dashboard" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="back-to-dashboard">
            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> Dashboard
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-12">
        <h1 className="font-headings text-5xl uppercase tracking-tight mb-2">Subscription</h1>
        <p className="font-body text-xs tracking-[0.2em] uppercase text-white/50 mb-10">Manage your Public Landowner AI<sup>TM</sup> access</p>

        {info && <div className="border border-plai-teal/30 bg-plai-teal/10 p-4 font-body text-sm text-plai-teal mb-6" data-testid="sub-info">{info}</div>}
        {err && <div className="border border-red-500/40 bg-red-500/10 p-4 font-body text-sm text-red-300 mb-6" data-testid="sub-error">{err}</div>}

        {sub ? (
          <div className="glass iridescent-border p-8" data-testid="active-sub-card">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="h-7 w-7 text-plai-teal" strokeWidth={1.5} />
              <h2 className="font-headings text-3xl uppercase">Active</h2>
            </div>
            <p className="font-body text-sm text-white/80 mb-6">Status: {status}</p>
            <p className="font-body text-sm text-white/70 mb-8">You have full access to the War Room, Notice of Intent generation, and Instagram Megaphone.</p>
            <button onClick={portal} disabled={busy} className="btn-ghost" data-testid="manage-billing-btn">
              <CreditCard className="h-4 w-4 inline mr-2" strokeWidth={1.5} /> Manage Billing & Cancel
            </button>
          </div>
        ) : (
          <div className="glass iridescent-border p-8" data-testid="subscribe-card">
            <h2 className="font-headings text-3xl uppercase mb-1">$9.99 / month</h2>
            <p className="font-body text-xs tracking-[0.2em] uppercase text-white/50 mb-6">For the price of a burrito</p>
            <ul className="space-y-3 mb-8">
              {[
                'Six-agent War Room debate',
                'Live 2026 legal research',
                'Time-stamped Notice of Intent PDFs',
                'Instagram Megaphone generator',
                'Cancel anytime',
              ].map((it, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-sm text-white/85">
                  <CheckCircle2 className="h-4 w-4 text-plai-teal mt-0.5 shrink-0" strokeWidth={1.5} />
                  {it}
                </li>
              ))}
            </ul>
            <button onClick={subscribe} disabled={busy} className="btn-primary-cta w-full text-base" data-testid="subscribe-btn">
              {busy ? 'Loading...' : 'Secure My Land'}
            </button>
          </div>
        )}

        <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/30 text-center mt-6">
          Payments powered by Stripe. Cancel anytime from the billing portal.
        </p>
      </main>

      <Footer />
    </div>
  );
}
